import { useState } from 'react'
import { api } from '../api/client'
import type { LookupItem } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { useAuth } from '../auth'
import { ConfirmDialog } from './ConfirmDialog'
import { Empty, Notice } from './ui'

/**
 * LookupCard maintains one dropdown list (业务员 / 平台) by id, not by name: the
 * rows already written keep the text they were written with, so deleting an entry
 * only takes it out of the dropdown. Both lists share the same API shape, so one
 * component serves them all.
 */
export function LookupCard({ path, label, hint }: { path: string; label: string; hint: string }) {
  const { user } = useAuth()
  const canWrite = !!user?.canWrite
  const { data, error: listError, loading, reload } = useAsync<{ items: LookupItem[] }>(
    () => api.get<{ items: LookupItem[] }>(`/api/${path}`),
    [path],
  )
  const items = data?.items ?? []

  const [draft, setDraft] = useState('')
  const addOp = useOperation<LookupItem>(`lk-add-${path}`)
  const [removeTarget, setRemoveTarget] = useState<LookupItem | null>(null)
  const removeOp = useOperation<{ removed: number }>(`lk-del-${path}`)

  async function add() {
    const res = await addOp.run((key) => api.post<LookupItem>(`/api/${path}`, { name: draft.trim() }, key))
    if (res) {
      setDraft('')
      reload()
    }
  }

  async function remove() {
    if (!removeTarget) return
    const res = await removeOp.run((key) =>
      api.post<{ removed: number }>(`/api/${path}/${removeTarget.id}/delete`, {}, key),
    )
    if (res) {
      setRemoveTarget(null)
      reload()
    }
  }

  return (
    <div className="card">
      <h2>{label}名单</h2>
      <p className="muted">{hint}</p>
      <Notice error={addOp.error} />
      {canWrite && (
        <div className="row wrap">
          <label className="field grow">
            <span>新增{label}</span>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={24}
              placeholder="名字（不能含空格或 , |）"
            />
          </label>
          <button
            type="button"
            className="primary"
            onClick={() => void add()}
            disabled={!draft.trim() || addOp.busy}
          >
            {addOp.busy ? '添加中…' : '添加'}
          </button>
        </div>
      )}
      {addOp.result && <p className="muted">已添加「{addOp.result.name}」。</p>}
      <Notice error={listError} />
      {loading && items.length === 0 && <p className="muted">载入中…</p>}
      {!loading && items.length === 0 && <Empty text={`名单为空，先添加${label}。`} />}
      {items.length > 0 && (
        <>
          <ul className="chips">
            {items.map((n) => (
              <li key={n.id}>
                {n.name}
                {canWrite && (
                  <button type="button" className="link" onClick={() => setRemoveTarget(n)}>
                    删除
                  </button>
                )}
              </li>
            ))}
          </ul>
          <p className="muted small">共 {items.length} 项，下拉里就是这个顺序。</p>
        </>
      )}

      {removeTarget && (
        <ConfirmDialog
          title={`从名单删除「${removeTarget.name}」`}
          description="之后的记录不能再选这个名字；已经写入的台账与库存保持不变，删除会记进审计。"
          confirmLabel="删除"
          busy={removeOp.busy}
          error={removeOp.error}
          onConfirm={() => void remove()}
          onClose={() => {
            setRemoveTarget(null)
            removeOp.newIntent()
          }}
        />
      )}
    </div>
  )
}
